import React, { useState, useCallback, useMemo } from "react";

import moment from "moment";
import * as Icon from "react-feather";

import emptyScheduleImg from "../../../assets/images/Timeline Week.png";
import { H5, H6, P } from "../../atoms/Texts";
import { PrimaryButton } from "../../atoms/Buttons";
import CreateCaseScheduleModal from "../../organisms/CreateCaseScheduleModal";
import CreateCaseScheduleSuccessModal from "../../organisms/CreateCaseScheduleSuccessModal";
import { CaseScheduleCard } from "../../atoms/Cards";
import UpdateCaseScheduleModal from "../../organisms/UpdateCaseScheduleModal";
import UpdateCaseScheduleSuccessModal from "../../organisms/UpdateCaseScheduleSuccessModal";
import DeleteCaseScheduleModal from "../../organisms/DeleteCaseScheduleModal";
import Joyride from "react-joyride";
import { useWalkthrough } from "../../../shared/hooks/useWalkthrough";

export const CaseSchedule = ({ details }) => {
  const [schedules] = useState([]);

  const steps = [
    {
      target: ".schedule",
      content: "This tab contains the appointment schedule",
    },
    {
      target: ".add-schedule",
      content: "You can set up a schedule right here",
    },
  ];

  const { joyrideProps } = useWalkthrough({
    steps,
    walkthroughKey: "case_detail_schedule",
    pageSpecific: true, // Each page tracks separately
  });

  // State management with more descriptive variable names
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [modalStates, setModalStates] = useState({
    deleteSchedule: false,
    updateSchedule: false,
    createSchedule: false,
    createScheduleSuccess: false,
    updateScheduleSuccess: false,
  });

  // Centralized modal toggle function
  const toggleModal = useCallback((modalName) => {
    setModalStates((prev) => ({
      ...prev,
      [modalName]: !prev[modalName],
    }));
  }, []);

  // Memoize complex render methods to prevent unnecessary re-renders
  const renderEmptyState = useMemo(
    () => () => (
      <div className="flex items-center justify-center flex-col bg-white py-32">
        <img src={emptyScheduleImg} alt="Empty schedule" />
        <H6>Empty Schedule</H6>
        <P color="#7D7B96">Set an appointment</P>
        <PrimaryButton
          p="14px 50px"
          className="mt-4 add-schedule"
          onClick={() => toggleModal("createSchedule")}
        >
          Set Appointment
        </PrimaryButton>
      </div>
    ),
    [toggleModal],
  );

  const renderScheduleDetails = useMemo(
    () => () => {
      if (!selectedSchedule) return null;

      return (
        <div className={`  ${selectedSchedule?.id ? "block py-10" : "hidden"}`}>
          <P color="#22202C" className="mb-4">
            You have a scheduled call with {details?.referral?.user?.firstName}
          </P>
          <P color="#22202C" fontSize="14px" smallSize="14px" className="mb-1">
            Meeting Details
          </P>
          <P color="#716E8B" fontSize="14px" smallSize="14px" className="mb-6">
            {selectedSchedule.locationDetails}
          </P>

          <div
            className="flex items-center rounded-lg p-8"
            style={{ background: "#F5F4FA" }}
          >
            <div className="w-1/2">
              <P color="#9390B0" fontSize="14px" smallSize="14px" className="mb-3">
                Date
              </P>
              <H5 color="#22202C" className="mt-0">
                {moment(selectedSchedule.date).format("ll")}
              </H5>
            </div>
            <div className="w-1/2 pl-6" style={{ borderLeft: "1px solid #B4B0D1" }}>
              <P color="#9390B0" fontSize="14px" smallSize="14px" className="mb-3">
                Time
              </P>
              <H5 color="#22202C" className="mt-0">
                {selectedSchedule.time}
              </H5>
            </div>
          </div>
          <div className="flex items-center mt-12">
            <PrimaryButton.Light
              type="button"
              className="flex-shrink-0 mr-4 "
              onClick={() => toggleModal("deleteSchedule")}
            >
              Cancel Meeting
            </PrimaryButton.Light>
            <PrimaryButton
              className="flex-grow"
              onClick={() => toggleModal("updateSchedule")}
            >
              Reschedule Meeting
            </PrimaryButton>
          </div>
        </div>
      );
    },
    [selectedSchedule, details, toggleModal],
  );

  const renderSchedulesList = useMemo(
    () => () => (
      <div className="bg-white px-8 py-10">
        {schedules?.map((schedule) => (
          <React.Fragment>
            <CaseScheduleCard
              key={schedule.id}
              onClick={() => setSelectedSchedule(schedule)}
              _case={details}
              schedule={schedule}
              selectedSchedule={selectedSchedule}
              setSelectedSchedule={setSelectedSchedule}
            />
            <div className="my-4" style={{ border: "1px solid #CECBE2" }} />
          </React.Fragment>
        ))}
      </div>
    ),
    [schedules, details, selectedSchedule],
  );

  const renderCreateAppointmentButton = useMemo(
    () => () => (
      <div className="flex items-center justify-end">
        <PrimaryButton.Light
          className="mt-4"
          onClick={() => toggleModal("createSchedule")}
        >
          <Icon.Plus color="#442ED0" size={18} className="mr-3 inline-block" />
          Create Appointment
        </PrimaryButton.Light>
      </div>
    ),
    [toggleModal],
  );

  const renderModals = useMemo(
    () => () => (
      <>
        {modalStates.createSchedule && (
          <CreateCaseScheduleModal
            showSuccessModal={() => toggleModal("createScheduleSuccess")}
            closeModal={() => toggleModal("createSchedule")}
          />
        )}
        {modalStates.createScheduleSuccess && (
          <CreateCaseScheduleSuccessModal
            _case={details}
            closeModal={() => window.location.reload()}
          />
        )}
        {modalStates.updateSchedule && (
          <UpdateCaseScheduleModal
            schedule={selectedSchedule}
            showSuccessModal={() => toggleModal("updateScheduleSuccess")}
            _case={details}
            closeModal={() => toggleModal("updateSchedule")}
          />
        )}
        {modalStates.updateScheduleSuccess && (
          <UpdateCaseScheduleSuccessModal
            _case={details}
            closeModal={() => window.location.reload()}
          />
        )}
        {modalStates.deleteSchedule && (
          <DeleteCaseScheduleModal
            schedule={selectedSchedule}
            closeModal={() => toggleModal("deleteSchedule")}
          />
        )}
      </>
    ),
    [modalStates, toggleModal, details, selectedSchedule],
  );

  // Main render method
  return (
    <>
      <Joyride
        key={joyrideProps.run ? "walkthrough-active" : "walkthrough-inactive"}
        {...joyrideProps}
      />

      <div className="schedule">
        {schedules?.length < 1 ? (
          renderEmptyState()
        ) : (
          <div>
            {renderCreateAppointmentButton()}
            <section className="grid grid-cols-2 gap-16">
              {renderSchedulesList()}
              {renderScheduleDetails()}
            </section>
          </div>
        )}
      </div>

      {renderModals()}
    </>
  );
};
